import React, { useEffect, useRef, useState } from 'react';
import DataTable from 'react-data-table-component';
import moment from 'moment';
import { Chip, Tooltip } from '@mui/material';
import { AiOutlineVerticalAlignBottom } from 'react-icons/ai';
import { getHoldingSettlements } from '../../helper/Api';
import { isValidURL } from '../../helper/Utils';
import './HoldingSettlementsList.css';

const TABLE_HEADER_HEIGHT = 56;
const TABLE_ROW_HEIGHT = 48;
const TABLE_PAGINATION_HEIGHT = 56;
const TABLE_CARD_PADDING = 32;
const TABLE_BOTTOM_GAP = 16;

const formatIndianAmount = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '';
    return Number(amount).toLocaleString('en-IN');
};

const YesPendingCell = ({ value }) => {
    const isYes = String(value) === '1';
    return (
        <span
            className={`holding-settlements-status-pill ${
                isYes
                    ? 'holding-settlements-status-pill--yes'
                    : 'holding-settlements-status-pill--pending'
            }`}
        >
            {isYes ? 'Yes' : 'Pending'}
        </span>
    );
};

const getDownloadUrl = (value) => {
    if (!value) return '';
    return isValidURL(String(value)) ? String(value) : '';
};

const getStatusChip = (status) => {
    const normalized = String(status || '').toLowerCase().trim();

    if (normalized === 'onhold' || normalized === 'on hold') {
        return <Chip label="On Hold" color="warning" size="small" />;
    }
    if (normalized === 'completed') {
        return <Chip label="Sent to Bank" color="success" size="small" />;
    }
    if (normalized === 'settled') {
        return <Chip label="Completed" color="secondary" size="small" />;
    }
    if (normalized === 'in progress' || normalized === 'in_progress' || normalized === 'inprogress') {
        return <Chip label="In Progress" color="primary" size="small" />;
    }

    return <Chip label="In Queue" color="primary" size="small" />;
};

const HoldingSettlementsList = () => {
    const tableContainerRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [records, setRecords] = useState([]);
    const [tableScrollHeight, setTableScrollHeight] = useState('400px');

    const customStyles = {
        headCells: {
            style: {
                fontWeight: 'bold',
                fontSize: '14px',
                backgroundColor: '#f8f9fc',
            },
        },
        cells: {
            style: {
                fontSize: '13px',
            },
        },
    };

    const handleDownload = (url) => {
        if (!url) return;
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const columns = [
        {
            name: 'Unique ID',
            selector: (row) => row.id,
            width: '150px',
        },
        {
            name: 'Sett. Date',
            selector: (row) => (row.settlement_date
                ? moment(row.settlement_date, 'YYYY-MM-DD').format('DD-MM-YYYY')
                : ''),
            width: '150px',
        },
        {
            name: 'Status',
            cell: (row) => getStatusChip(row.status),
            center: true,
            width: '130px',
        },
        {
            name: 'Amount',
            cell: (row) => (
                <span className="holding-settlements-amount">
                    {formatIndianAmount(row.amount)}
                </span>
            ),
            right: true,
            width: '130px',
        },
        {
            name: 'Account No',
            selector: (row) => row.account_no,
            wrap: true,
            minWidth: '150px',
        },
        {
            name: 'Mail Sent',
            cell: (row) => <YesPendingCell value={row.is_mail_sent} />,
            center: true,
            width: '110px',
        },
        {
            name: 'File Shared',
            cell: (row) => <YesPendingCell value={row.is_file_shared} />,
            center: true,
            width: '110px',
        },
        {
            name: 'Actions',
            cell: (row) => {
                const mailUrl = getDownloadUrl(row.mail_file_path || row.mail_file_url);
                const fileUrl = getDownloadUrl(row.file_url);

                return (
                    <div className="holding-settlements-actions">
                        <Tooltip title="Download Mail Attachment" arrow placement="top">
                            <span className="holding-settlements-action-tooltip-wrap">
                                <button
                                    type="button"
                                    className="holding-settlements-action-btn holding-settlements-action-btn--mail"
                                    disabled={!mailUrl}
                                    onClick={() => handleDownload(mailUrl)}
                                >
                                    <AiOutlineVerticalAlignBottom size={16} />
                                </button>
                            </span>
                        </Tooltip>
                        <Tooltip title="Download Settlement File" arrow placement="top">
                            <span className="holding-settlements-action-tooltip-wrap">
                                <button
                                    type="button"
                                    className="holding-settlements-action-btn holding-settlements-action-btn--file"
                                    disabled={!fileUrl}
                                    onClick={() => handleDownload(fileUrl)}
                                >
                                    <AiOutlineVerticalAlignBottom size={16} />
                                </button>
                            </span>
                        </Tooltip>
                    </div>
                );
            },
            center: true,
            width: '110px',
        },
        {
            name: 'Created At',
            selector: (row) => (row.created_at
                ? moment(row.created_at, ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601]).format('DD-MM-YYYY hh:mm A')
                : ''),
            wrap: true,
            minWidth: '170px',
        },
    ];

    const fetchSettlements = async () => {
        try {
            setLoading(true);
            const response = await getHoldingSettlements();
            if (response.data.status) {
                setRecords(response.data.result || []);
            } else {
                setRecords([]);
            }
        } catch (error) {
            setRecords([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSettlements();
    }, []);

    useEffect(() => {
        const updateTableHeight = () => {
            const rowCount = Math.min(records.length || 1, rowsPerPage);
            const bodyContentHeight = rowCount * TABLE_ROW_HEIGHT;
            const containerTop = tableContainerRef.current?.getBoundingClientRect().top ?? 220;
            const maxBodyHeight = window.innerHeight
                - containerTop
                - TABLE_HEADER_HEIGHT
                - TABLE_PAGINATION_HEIGHT
                - TABLE_CARD_PADDING
                - TABLE_BOTTOM_GAP;
            const nextHeight = Math.min(
                bodyContentHeight,
                Math.max(maxBodyHeight, 120)
            );
            setTableScrollHeight(`${nextHeight}px`);
        };

        updateTableHeight();
        window.addEventListener('resize', updateTableHeight);
        return () => window.removeEventListener('resize', updateTableHeight);
    }, [records.length, rowsPerPage, loading]);

    return (
        <div ref={tableContainerRef} className="holding-settlements-datatable-root card p-3">
            <DataTable
                customStyles={customStyles}
                className="dataTables_wrapper"
                columns={columns}
                data={records}
                progressPending={loading}
                sortable={false}
                fixedHeader
                fixedHeaderScrollHeight={tableScrollHeight}
                pagination
                paginationPerPage={rowsPerPage}
                paginationTotalRows={records.length}
                paginationRowsPerPageOptions={[100, 200, 500, 1000]}
                onChangeRowsPerPage={setRowsPerPage}
                noDataComponent={
                    <div className="p-4 text-muted">No holding settlement records found.</div>
                }
            />
        </div>
    );
};

export default HoldingSettlementsList;
